// src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import HomePage from './components/HomePage';
import SearchPage from './components/SearchPage';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import { AuthProvider, useAuth } from './context/AuthContext';
import './index.css';
import LoginModal from './components/auth/LoginModal';
import Recommendations from './components/Recommendations';
import Navbar from './components/Navbar';
import AdminDashboard from './components/admin/AdminDashboard';
import TermsOfService from './components/legal/TermsOfService';
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import Contact from './components/Contact';
import Footer from './components/Footer';
import FloatingContactButton from './components/FloatingContactButton';
import BlogList from './components/blog/BlogList';
import BlogPost from './components/blog/BlogPost';
import Profile from './components/Profile';
import GameDetails from './components/GameDetails';
//import { HelmetProvider } from 'react-helmet-async';

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen bg-gray-900 flex justify-center items-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
    </div>;
  }
  
  if (!currentUser) {
    return (
      <>
        <LoginModal 
          isOpen={true} 
          onClose={() => window.location.href = "/"} 
        />
        <div className="min-h-screen bg-gray-900 flex justify-center items-center">
          <p className="text-gray-300">Please log in to access this page.</p>
        </div>
      </>
    );
  }
  
  return children;
};

// Track routes for GA
const RouteTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Track pageview with Google Analytics
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search
      });
    }
  }, [location]);
  
  return null;
};

function App() {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginCallback, setLoginCallback] = useState(null);
  const [resetSearchKey, setResetSearchKey] = useState(0);
  
  const handleResetSearch = () => {
    console.log('Reset search triggered from navbar');
    // Only reset if there are active search params
    //const currentSearchParams = new URLSearchParams(window.location.search);
    //if (currentSearchParams.toString()) {
      // Then trigger component reset
      setResetSearchKey(prevKey => prevKey + 1);
    //}
  };

  const showLogin = (callback = null) => {
    setLoginCallback(() => callback);
    setShowLoginModal(true);
  };

  const handleLoginSuccess = () => {
    if (loginCallback && typeof loginCallback === 'function') {
      loginCallback();
    }
    setLoginCallback(null);
  };

  // The AppRoutes component handles location state properly
  const AppRoutes = () => {
    //const location = useLocation();
    
    // COMMENTED OUT: Modal logic
    /* 
    // Determine if we're showing a modal
    const isModalRoute = 
      location.state && 
      location.state.modal && 
      location.state.backgroundLocation;
    
    // Store the background location for use with the modal
    const backgroundLocation = isModalRoute ? location.state.backgroundLocation : location;
    */
    
    return (
      <>
        <Routes>
          <Route path="/" element={<HomePage showLogin={showLogin} />} />
          <Route path="/search" element={<SearchPage resetTrigger={resetSearchKey} showLogin={showLogin} />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/profile" element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } />
          <Route path="/recommendations" element={
            <ProtectedRoute>
              <Recommendations showLogin={showLogin} />
            </ProtectedRoute>
          } />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/users" element={
            <ProtectedRoute>
              <AdminDashboard initialTab="users" />
            </ProtectedRoute>
          } />
          <Route path="/admin/recommendations" element={
            <ProtectedRoute>
              <AdminDashboard initialTab="recommendations" />
            </ProtectedRoute>
          } />
          <Route path="/admin/games" element={
            <ProtectedRoute>
              <AdminDashboard initialTab="games" />
            </ProtectedRoute>
          } />
          <Route path="/admin/messages" element={
            <ProtectedRoute>
              <AdminDashboard initialTab="messages" />
            </ProtectedRoute>
          } />
          <Route path="/admin/blog" element={
            <ProtectedRoute>
              <AdminDashboard initialTab="blog" />
            </ProtectedRoute>
          } />
          <Route path="/admin/functions" element={
            <ProtectedRoute>
              <AdminDashboard initialTab="functions" />
            </ProtectedRoute>
          } />
          <Route path="/game/:slug" element={<GameDetails showLogin={showLogin} />} />
        </Routes>
      </>
    );
  };

  return (
    <Router>
        <AuthProvider>
          <div className="flex flex-col min-h-screen bg-gray-900">
            <RouteTracker /> 
            <main className="flex-grow">
              <Navbar showLogin={showLogin} onResetSearch={handleResetSearch} />
              <AppRoutes />
            </main>
            <FloatingContactButton />
            <LoginModal
              isOpen={showLoginModal}
              onClose={() => setShowLoginModal(false)}
              onLoginSuccess={handleLoginSuccess}
            />
            <Footer />
          </div>
        </AuthProvider>
    </Router>
  );
}

export default App;